/* eslint-disable react/no-unescaped-entities */
import { Link } from 'react-router-dom';
import {
  Split, Users, Home, Vote, Coins, FileText, Scale, ScrollText,
} from 'lucide-react';
import GuidePage from '@/components/library/GuidePage';

const config = {
  path: '/indivision',
  slug: 'indivision',
  breadcrumbLabel: 'Indivision',
  readingTime: '7 min',
  lastUpdated: '18 février 2026',
  seoTitle: "Indivision : droits, sortie et partage | HÉRITA",
  seoDescription:
    "Comprenez facilement une convention d'indivision, une sortie d'indivision ou un acte de partage. HÉRITA vous explique vos droits et obligations dans l'indivision.",

  hero: {
    eyebrow: 'Guides HÉRITA',
    title: 'Comprendre une indivision',
    subtitle:
      "L'indivision survient dès lors que plusieurs personnes possèdent ensemble un bien sans qu'il soit divisé. HÉRITA vous aide à comprendre vos droits, vos obligations et les options qui s'offrent à vous pour en sortir.",
  },

  definition: {
    eyebrow: 'Définition',
    title: "Qu'est-ce qu'une indivision\u00A0?",
    paragraphs: [
      <>
        Une indivision est la situation juridique dans laquelle{' '}
        <strong className="font-semibold text-[#1A2530]">plusieurs personnes possèdent ensemble un même bien</strong>
        {' '}sans qu'aucune n'en détienne une part matériellement
        identifiable. Chacun a une <em>quote-part</em> exprimée en fraction
        (1/3, 1/4, 50 %) — mais pas «&nbsp;la chambre du fond&nbsp;» ou
        «&nbsp;le jardin&nbsp;».
      </>,
      <>
        L'indivision naît le plus souvent à l'occasion d'une{' '}
        <em>succession</em>&nbsp;: au décès d'un parent, ses biens sont
        recueillis par ses héritiers ensemble, et tant que le partage n'a pas
        été organisé, ils sont en indivision. Elle peut aussi naître d'un
        achat à plusieurs (entre concubins, entre frères et sœurs) ou d'une
        séparation.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Quels sont les droits d'un indivisaire&nbsp;?</strong>{' '}
        Chacun peut user du bien dans le respect des autres&nbsp;: c'est-à-dire
        à condition de ne pas en priver les co-indivisaires. Chacun a aussi
        droit à sa part des revenus (loyers, fermages) en proportion de sa
        quote-part. Mais aucune décision majeure ne peut être prise seul.
      </>,
      <>
        <strong className="font-semibold text-[#1A2530]">Qui décide quoi&nbsp;?</strong>{' '}
        Trois règles. Les <em>actes d'administration courante</em> (entretien,
        location courte durée) se prennent à la majorité des deux tiers. Les{' '}
        <em>actes de disposition</em> (vendre, faire un crédit) exigent
        l'unanimité. Et chaque indivisaire peut toujours, à tout moment,
        demander le <em>partage</em>&nbsp;: la sortie de l'indivision.
      </>,
      <>
        Une <em>convention d'indivision</em> peut être signée pour organiser
        la vie collective&nbsp;: désigner un gérant, fixer les règles de
        vote, prévoir une durée déterminée, encadrer les cessions de parts.
        Elle est conseillée dès que plus de deux personnes sont concernées.
      </>,
      <>
        Sortir de l'indivision, c'est l'objet du <em>partage</em>. Il peut
        être amiable (les indivisaires se mettent d'accord) ou judiciaire
        (un juge tranche). HÉRITA vous aide à comprendre votre place actuelle
        dans l'indivision et les options disponibles, dans l'optique d'une
        future{' '}
        <Link to="/declaration-succession" className="text-[#1A2530] font-medium underline underline-offset-[5px] decoration-[#D3CCBE] hover:decoration-[#1A2530]">déclaration de succession</Link>
        {' '}ou d'une vente.
      </>,
    ],
  },

  usage: {
    eyebrow: 'Utilité',
    title: "À quoi sert ce document\u00A0?",
    items: [
      { title: 'Constater la situation', description: "L'acte établit qui détient quoi, dans quelle proportion, sur quel bien. C'est la photographie juridique du moment." },
      { title: 'Organiser le quotidien', description: "Une convention permet de fixer les règles de gestion, désigner un gérant, prévoir une durée déterminée." },
      { title: 'Préparer la sortie', description: "L'acte de partage met fin à l'indivision en attribuant à chacun des biens identifiés en pleine propriété." },
      { title: 'Sécuriser les ventes', description: "Sans accord unanime, aucun bien indivis ne peut être vendu. La convention précise les conditions et droits de préemption." },
    ],
  },

  content: {
    eyebrow: 'Contenu',
    title: "Que contient un acte d'indivision\u00A0?",
    intro: "Plusieurs documents peuvent encadrer une indivision. Voici les éléments essentiels que l'on retrouve dans chacun.",
    cards: [
      { Icon: Users, title: 'Indivisaires', description: "Identité de chaque co-propriétaire, lien éventuel avec les autres, capacité juridique." },
      { Icon: Split, title: 'Quotes-parts', description: "Fraction du bien détenue par chacun, exprimée en parts (1/3, 1/4) ou en pourcentage." },
      { Icon: Home, title: 'Bien(s) indivis', description: "Description complète des biens : adresse, surface, références cadastrales, lots de copropriété." },
      { Icon: ScrollText, title: 'Origine de l\'indivision', description: "Succession, achat conjoint, divorce, donation : la cause juridique est rappelée." },
      { Icon: Vote, title: 'Règles de décision', description: "Modalités de vote : unanimité, majorité simple, majorité qualifiée selon la nature des décisions." },
      { Icon: Scale, title: 'Gestion', description: "Désignation éventuelle d'un gérant, durée de son mandat, étendue de ses pouvoirs." },
      { Icon: Coins, title: 'Partage des revenus', description: "Quote-part de chacun dans les loyers, fermages et autres fruits du bien." },
      { Icon: FileText, title: 'Sortie d\'indivision', description: "Conditions de retrait, droit de préemption, modalités d'évaluation et de paiement éventuel." },
    ],
  },

  checks: {
    eyebrow: 'Vigilance',
    title: 'Les points importants à vérifier',
    intro: "L'indivision est génératrice de conflits. Quelques points méritent une attention particulière avant tout engagement.",
    items: [
      "Vérifier la quote-part exacte de chaque indivisaire et la cohérence avec l'acte de notoriété.",
      "Identifier qui a la jouissance effective du bien et à quelles conditions.",
      "Comprendre les règles de vote — majorité des 2/3 pour la gestion, unanimité pour la disposition.",
      "Lire toute clause de durée déterminée : la convention peut être limitée à 5 ans, renouvelable.",
      "Repérer un éventuel droit de préemption en cas de cession.",
      "S'assurer que l'indivision est bien publiée si elle porte sur un bien immobilier.",
    ],
  },

  autoChecks: {
    eyebrow: 'Sans rien demander',
    title: 'Ce que HÉRITA identifie automatiquement',
    intro: "Dès l'import de l'acte, HÉRITA en extrait les éléments clés.",
    items: [
      'Indivisaires et quotes-parts',
      'Biens concernés et leur valeur',
      "Origine de l'indivision",
      'Règles de gestion',
      "Désignation d'un gérant éventuel",
      'Conditions de cession',
      'Options de sortie',
      'Questions à poser à votre notaire',
    ],
  },

  example: {
    eyebrow: 'Avant · Après',
    title: 'Un exemple simplifié',
    intro: "À gauche, un extrait fictif de convention d'indivision. À droite, ce qu'HÉRITA en aurait expliqué.",
    extract: (
      <>
        «&nbsp;Suite à la succession de Monsieur Paul ROUSSEAU, Madame Claire
        ROUSSEAU (3/8), Monsieur Marc ROUSSEAU (3/8) et Mademoiselle Léa
        ROUSSEAU (2/8) recueillent en indivision la maison sise 4 chemin
        des Vignes à Aix-en-Provence (13100), évaluée à QUATRE CENT MILLE
        EUROS (400 000 €). Les indivisaires conviennent d'une indivision
        organisée pour une durée de cinq années, avec désignation de Madame
        Claire ROUSSEAU comme gérante. Toute cession de quote-part est
        soumise au droit de préemption des autres indivisaires.&nbsp;»
      </>
    ),
    headline: "Ce que cela signifie pour Léa",
    bullets: [
      { strong: 'Vous détenez 2/8 de la maison', rest: ' soit 25 %, pour une valeur indicative de 100 000 €.' },
      { strong: "Vous n'êtes pas propriétaire d'un endroit précis", rest: ", mais bien d'une fraction du bien entier." },
      { strong: 'Votre tante Claire est gérante', rest: ' : elle prend les décisions de gestion courante au nom de tous.' },
      { strong: 'Pour 5 ans', rest: ', la convention organise la vie de l\'indivision et limite les conflits éventuels.' },
      { strong: 'Si vous voulez vendre votre part', rest: ', vos co-indivisaires ont un droit de préemption — ils peuvent racheter en priorité.' },
    ],
  },

  faq: [
    { q: "Qu'est-ce qu'une indivision ?", a: "C'est une situation juridique où plusieurs personnes possèdent ensemble un bien sans qu'il soit matériellement divisé. Chacun détient une quote-part (fraction) du bien entier, et non un endroit identifiable." },
    { q: "Comment naît une indivision ?", a: "Le plus souvent à la suite d'une succession. Elle peut aussi naître d'un achat conjoint (concubins, frères et sœurs) ou d'une séparation. Toute fois plusieurs personnes détiennent un même bien sans partage, il y a indivision." },
    { q: "Quelles sont les règles de décision ?", a: "Les actes d'administration courante (entretien, baux courts) se prennent à la majorité des deux tiers. Les actes de disposition (vente, hypothèque) exigent l'unanimité. Chacun peut toujours demander le partage." },
    { q: "Comment sortir de l'indivision ?", a: "Trois options. Le partage amiable, lorsque tous se mettent d'accord. Le partage judiciaire, demandé devant le juge en cas de blocage. La cession de sa quote-part à un autre indivisaire ou à un tiers (sous droit de préemption éventuel)." },
    { q: "Comment HÉRITA l'analyse-t-il ?", a: "HÉRITA identifie les indivisaires, leurs quotes-parts, les biens concernés, les règles de gestion, la désignation d'un gérant et les options de sortie. Vous obtenez en quelques minutes une lecture claire de votre situation et de vos marges de manœuvre." },
    { q: "Peut-on être contraint de rester en indivision ?", a: "Non. Le principe fondamental est que « nul ne peut être contraint à demeurer dans l'indivision ». Chaque indivisaire peut, à tout moment, demander le partage — sauf à avoir signé une convention d'indivision à durée déterminée (5 ans maximum, renouvelable)." },
    { q: "Qui paie les charges du bien ?", a: "Chaque indivisaire paie sa quote-part des charges (taxe foncière, charges de copropriété, entretien). Celui qui occupe seul le bien doit en principe une indemnité d'occupation aux autres." },
    { q: "Peut-on vendre sa quote-part ?", a: "Oui, mais les autres indivisaires bénéficient en général d'un droit de préemption : ils peuvent racheter votre quote-part en priorité, au prix proposé à un tiers. La cession à un tiers est libre sinon, sous réserve des statuts." },
    { q: "Faut-il un notaire pour une convention d'indivision ?", a: "Obligatoirement si l'indivision porte sur un bien immobilier. Le notaire rédige la convention, la publie au service de la publicité foncière et conseille les indivisaires sur les clauses adaptées." },
    { q: "Quel rapport avec une SCI ?", a: "La SCI est souvent présentée comme une alternative à l'indivision. La SCI est organisée et stable, l'indivision est subie et précaire. Transformer une indivision en SCI permet d'éviter les blocages et de structurer la transmission." },
  ],

  finalCta: {
    title: "Vous avez reçu un acte d'indivision\u00A0?",
    subtitle: "Déposez votre document. HÉRITA identifie automatiquement votre quote-part, vos droits, vos obligations et les options de sortie disponibles afin de vous fournir une lecture claire et personnalisée.",
  },
};

const Indivision = () => <GuidePage config={config} />;
export default Indivision;
